import { supabase } from '../lib/supabase';
import { TRAINING_CONTENT_STATUS, TRAINING_ROW_SLUGS } from '../constants/training';
import { chaptersToDb, normalizeChapters } from '../utils/videoTime';
import { computeAspectRatio, cropSettingsToDb } from '../utils/reelCrop';
import { seededShuffle } from '../utils/reelFeedSort';

const PLACEHOLDER_THUMB =
  'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';

const LEVEL_LABELS = {
  new_associate: 'Nuevos Agentes',
  licensed: 'Licenciados',
  both: 'Todos los niveles',
};

const REEL_SORT_COLUMNS = {
  recent: { column: 'published_at', ascending: false },
  viewed: { column: 'view_count', ascending: false },
  liked: { column: 'like_count', ascending: false },
};

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '—';
  const mins = Math.round(seconds / 60);
  return `${mins} min`;
}

function mapProfile(profile) {
  if (!profile) return null;
  return Array.isArray(profile) ? profile[0] : profile;
}

export function mapSessionRow(row) {
  const uploader = mapProfile(row.uploader);
  const speaker = row.speaker_name?.trim() || uploader?.full_name || 'GFI Latino';
  return {
    id: row.id,
    title: row.title,
    speaker,
    speakerName: row.speaker_name ?? '',
    level: LEVEL_LABELS[row.level] || row.level,
    levelValue: row.level,
    duration: formatDuration(row.duration_seconds),
    thumbnail: row.thumbnail_url || PLACEHOLDER_THUMB,
    thumbnailUrl: row.thumbnail_url ?? null,
    summary: row.summary,
    actionPoints: row.action_items ?? [],
    category: row.tags?.[0] || 'Entrenamiento',
    tags: row.tags ?? [],
    transcript: row.transcript,
    publitioVideoId: row.publitio_video_id,
    videoUrl: row.video_url,
    durationSeconds: row.duration_seconds ?? null,
    chapters: normalizeChapters(row.chapters),
    uploadedBy: row.uploaded_by,
    status: row.status ?? TRAINING_CONTENT_STATUS.PUBLISHED,
    isDraft: row.status === TRAINING_CONTENT_STATUS.DRAFT,
    publishedAt: row.published_at,
    updatedAt: row.updated_at,
    complianceConfirmed: Boolean(row.compliance_confirmed),
    archivedAt: row.archived_at,
    isArchived: Boolean(row.archived_at),
  };
}

export function mapReelRow(row, { likedByMe = false } = {}) {
  const uploader = mapProfile(row.uploader);
  const session = row.session;
  return {
    id: row.id,
    title: row.caption || session?.title || 'Clip de entrenamiento',
    caption: row.caption ?? '',
    speaker: uploader?.full_name || 'GFI Latino',
    likes: row.like_count ?? 0,
    comments: row.comment_count ?? 0,
    videoThumbnail: row.thumbnail_url || session?.thumbnail_url || PLACEHOLDER_THUMB,
    fullSessionId: row.session_id,
    tags: session?.tags ?? row.tags ?? [],
    likedByMe,
    viewCount: row.view_count ?? 0,
    publitioVideoId: row.publitio_video_id,
    videoUrl: row.video_url,
    uploadedBy: row.uploaded_by,
    status: row.status ?? TRAINING_CONTENT_STATUS.PUBLISHED,
    isDraft: row.status === TRAINING_CONTENT_STATUS.DRAFT,
    publishedAt: row.published_at,
    updatedAt: row.updated_at,
    clipStartSeconds: row.clip_start_seconds != null ? Number(row.clip_start_seconds) : null,
    clipEndSeconds: row.clip_end_seconds != null ? Number(row.clip_end_seconds) : null,
    sourceAspectRatio: row.source_aspect_ratio != null ? Number(row.source_aspect_ratio) : null,
    cropMode: row.crop_mode ?? null,
    focalX: row.focal_x != null ? Number(row.focal_x) : null,
    focalY: row.focal_y != null ? Number(row.focal_y) : null,
    archivedAt: row.archived_at,
    isArchived: Boolean(row.archived_at),
  };
}

const SESSION_SELECT = `
  id,
  title,
  summary,
  speaker_name,
  publitio_video_id,
  video_url,
  thumbnail_url,
  duration_seconds,
  transcript,
  action_items,
  tags,
  level,
  chapters,
  compliance_confirmed,
  uploaded_by,
  status,
  published_at,
  updated_at,
  archived_at,
  uploader:gfi_profiles!gfi_training_sessions_uploaded_by_fkey (
    id,
    full_name,
    avatar_url
  )
`;

const REEL_SELECT = `
  id,
  publitio_video_id,
  video_url,
  thumbnail_url,
  session_id,
  clip_start_seconds,
  clip_end_seconds,
  caption,
  source_aspect_ratio,
  crop_mode,
  focal_x,
  focal_y,
  like_count,
  view_count,
  status,
  published_at,
  updated_at,
  uploaded_by,
  archived_at,
  uploader:gfi_profiles!gfi_training_reels_uploaded_by_fkey (
    id,
    full_name,
    avatar_url
  ),
  session:gfi_training_sessions (
    id,
    title,
    thumbnail_url,
    tags,
    status
  )
`;

export async function fetchSessions({ tag, uploaderId, level, search, limit = 50 } = {}) {
  if (search?.trim()) {
    const { data, error } = await supabase.rpc('gfi_search_training_sessions', {
      search_query: search.trim(),
      tag_filter: tag || null,
      uploader_id: uploaderId || null,
      level_filter: level || null,
      result_limit: limit,
    });
    if (error) throw error;
    const ids = (data ?? []).map((r) => r.id);
    if (ids.length === 0) return [];
    const { data: rows, error: joinError } = await supabase
      .from('gfi_training_sessions')
      .select(SESSION_SELECT)
      .in('id', ids);
    if (joinError) throw joinError;
    const byId = Object.fromEntries((rows ?? []).map((r) => [r.id, r]));
    return ids.map((id) => mapSessionRow(byId[id])).filter(Boolean);
  }

  let query = supabase
    .from('gfi_training_sessions')
    .select(SESSION_SELECT)
    .eq('status', TRAINING_CONTENT_STATUS.PUBLISHED)
    .is('archived_at', null)
    .order('published_at', { ascending: false })
    .limit(limit);

  if (tag) query = query.contains('tags', [tag]);
  if (uploaderId) query = query.eq('uploaded_by', uploaderId);
  if (level) query = query.or(`level.eq.${level},level.eq.both`);

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map(mapSessionRow);
}

export async function fetchSessionById(id) {
  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .select(SESSION_SELECT)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data ? mapSessionRow(data) : null;
}

export async function fetchReels({
  sort = 'recent',
  shuffleSeed = null,
  limit = 10,
  offset = 0,
  userId = null,
} = {}) {
  let data;

  if (sort === 'random') {
    const { data: rows, error } = await supabase
      .from('gfi_training_reels')
      .select(REEL_SELECT)
      .eq('status', TRAINING_CONTENT_STATUS.PUBLISHED)
      .is('archived_at', null)
      .order('id', { ascending: true });

    if (error) throw error;

    const shuffled = seededShuffle(rows ?? [], shuffleSeed ?? 0);
    data = shuffled.slice(offset, offset + limit);
  } else {
    const { column, ascending } = REEL_SORT_COLUMNS[sort] ?? REEL_SORT_COLUMNS.recent;

    const { data: rows, error } = await supabase
      .from('gfi_training_reels')
      .select(REEL_SELECT)
      .eq('status', TRAINING_CONTENT_STATUS.PUBLISHED)
      .is('archived_at', null)
      .order(column, { ascending })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    data = rows ?? [];
  }

  let likedSet = new Set();
  if (userId && data?.length) {
    const { data: likes } = await supabase
      .from('gfi_training_reel_likes')
      .select('reel_id')
      .eq('user_id', userId)
      .in('reel_id', data.map((r) => r.id));
    likedSet = new Set((likes ?? []).map((l) => l.reel_id));
  }

  return (data ?? []).map((row) => mapReelRow(row, { likedByMe: likedSet.has(row.id) }));
}

export async function fetchReelById(id) {
  const { data, error } = await supabase
    .from('gfi_training_reels')
    .select(REEL_SELECT)
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return data ? mapReelRow(data) : null;
}

export async function updateReelCrop(reelId, { sourceAspectRatio, cropMode, focalX, focalY }) {
  const cropFields = cropSettingsToDb({
    sourceAspectRatio,
    cropMode,
    focalX,
    focalY,
  });

  const { data, error } = await supabase
    .from('gfi_training_reels')
    .update(cropFields)
    .eq('id', reelId)
    .select(REEL_SELECT)
    .single();

  if (error) throw error;
  return mapReelRow(data);
}

export async function recordReelView(reelId) {
  const { error } = await supabase.rpc('gfi_record_reel_view', { target_reel_id: reelId });
  if (error) throw error;
}

export async function toggleReelLike(reelId, currentlyLiked) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');

  if (currentlyLiked) {
    const { error } = await supabase
      .from('gfi_training_reel_likes')
      .delete()
      .eq('reel_id', reelId)
      .eq('user_id', user.id);
    if (error) throw error;
    return false;
  }

  const { error } = await supabase
    .from('gfi_training_reel_likes')
    .insert({ reel_id: reelId, user_id: user.id });
  if (error) throw error;
  return true;
}

export async function fetchPathsWithSessions() {
  const { data: paths, error: pathsError } = await supabase
    .from('gfi_training_paths')
    .select('id, title, description, sort_order')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (pathsError) throw pathsError;
  if (!paths?.length) return [];

  const pathIds = paths.map((p) => p.id);
  const { data: links, error: linksError } = await supabase
    .from('gfi_training_path_sessions')
    .select('path_id, position, session_id')
    .in('path_id', pathIds)
    .eq('status', 'approved')
    .order('position', { ascending: true });

  if (linksError) throw linksError;

  const sessionIds = [...new Set((links ?? []).map((l) => l.session_id))];
  if (sessionIds.length === 0) {
    return paths.map((p) => ({
      id: p.id,
      name: p.title,
      description: p.description,
      sessions: [],
      isComingSoon: true,
    }));
  }

  const { data: sessions, error: sessionsError } = await supabase
    .from('gfi_training_sessions')
    .select(SESSION_SELECT)
    .in('id', sessionIds)
    .eq('status', TRAINING_CONTENT_STATUS.PUBLISHED)
    .is('archived_at', null);

  if (sessionsError) throw sessionsError;

  const sessionMap = Object.fromEntries((sessions ?? []).map((s) => [s.id, mapSessionRow(s)]));

  return paths.map((path) => {
    const pathLinks = (links ?? []).filter((l) => l.path_id === path.id);
    const pathSessions = pathLinks.map((l) => sessionMap[l.session_id]).filter(Boolean);
    return {
      id: path.id,
      name: path.title,
      description: path.description,
      sessions: pathSessions,
      isComingSoon: pathSessions.length === 0,
    };
  });
}

export async function fetchTrainingPaths() {
  const { data, error } = await supabase
    .from('gfi_training_paths')
    .select('id, title, sort_order')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []).map((p) => ({ id: p.id, title: p.title }));
}

export async function suggestSessionPathLink(sessionId, pathId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');

  const { error } = await supabase
    .from('gfi_training_path_sessions')
    .insert({
      path_id: pathId,
      session_id: sessionId,
      status: 'pending',
      suggested_by: user.id,
      position: null,
    });

  if (error) {
    if (error.code === '23505') return;
    throw error;
  }
}

export async function fetchSessionPathLinks(sessionId) {
  const { data, error } = await supabase
    .from('gfi_training_path_sessions')
    .select(`
      path_id,
      session_id,
      position,
      status,
      suggested_by,
      reviewed_at,
      path:gfi_training_paths!gfi_training_path_sessions_path_id_fkey (
        id,
        title
      ),
      suggester:gfi_profiles!gfi_training_path_sessions_suggested_by_fkey (
        id,
        full_name
      )
    `)
    .eq('session_id', sessionId)
    .in('status', ['pending', 'approved'])
    .order('status', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => ({
    pathId: row.path_id,
    sessionId: row.session_id,
    position: row.position,
    status: row.status,
    pathTitle: row.path?.title ?? '',
    suggesterName: row.suggester?.full_name ?? null,
    reviewedAt: row.reviewed_at,
  }));
}

export async function approveSessionPathLink(pathId, sessionId) {
  const { error } = await supabase.rpc('gfi_approve_path_session_link', {
    p_path_id: pathId,
    p_session_id: sessionId,
  });
  if (error) throw error;
}

export async function rejectSessionPathLink(pathId, sessionId) {
  const { error } = await supabase.rpc('gfi_reject_path_session_link', {
    p_path_id: pathId,
    p_session_id: sessionId,
  });
  if (error) throw error;
}

export async function adminLinkSessionToPath(pathId, sessionId, position = null) {
  const { error } = await supabase.rpc('gfi_admin_link_session_to_path', {
    p_path_id: pathId,
    p_session_id: sessionId,
    p_position: position,
  });
  if (error) throw error;
}

export async function adminUnlinkSessionFromPath(pathId, sessionId) {
  const { error } = await supabase
    .from('gfi_training_path_sessions')
    .delete()
    .eq('path_id', pathId)
    .eq('session_id', sessionId);

  if (error) throw error;
}

export async function fetchTrainingPathsForAdmin() {
  return fetchTrainingPaths();
}

export async function fetchPathWithSessions(pathId) {
  const { data: path, error: pathError } = await supabase
    .from('gfi_training_paths')
    .select('id, title, description, sort_order')
    .eq('id', pathId)
    .maybeSingle();

  if (pathError) throw pathError;
  if (!path) return null;

  const { data: links, error: linksError } = await supabase
    .from('gfi_training_path_sessions')
    .select('path_id, position, session_id')
    .eq('path_id', pathId)
    .eq('status', 'approved')
    .order('position', { ascending: true });

  if (linksError) throw linksError;

  if (!links?.length) {
    return {
      id: path.id,
      name: path.title,
      description: path.description,
      sessions: [],
      isComingSoon: true,
    };
  }

  const sessionIds = links.map((l) => l.session_id);
  const { data: sessions, error: sessionsError } = await supabase
    .from('gfi_training_sessions')
    .select(SESSION_SELECT)
    .in('id', sessionIds)
    .eq('status', TRAINING_CONTENT_STATUS.PUBLISHED)
    .is('archived_at', null);

  if (sessionsError) throw sessionsError;

  const sessionMap = Object.fromEntries((sessions ?? []).map((s) => [s.id, mapSessionRow(s)]));
  const pathSessions = links.map((l) => sessionMap[l.session_id]).filter(Boolean);

  return {
    id: path.id,
    name: path.title,
    description: path.description,
    sessions: pathSessions,
    isComingSoon: pathSessions.length === 0,
  };
}

export async function fetchUncoveredSessions({ limit = 200 } = {}) {
  const [paths, allSessions] = await Promise.all([
    fetchPathsWithSessions(),
    fetchSessions({ limit }),
  ]);

  const pathSessionIds = new Set(
    paths.flatMap((path) => path.sessions.map((s) => s.id)),
  );

  return allSessions.filter((session) => !pathSessionIds.has(session.id));
}

export async function fetchTrainingRowList(rowId, userId) {
  if (rowId === TRAINING_ROW_SLUGS.CONTINUE) {
    if (!userId) {
      return { title: 'Continuar Viendo', sessions: [], isComingSoon: false };
    }
    const sessions = await fetchContinueWatchingSessions(userId, 100);
    return {
      title: 'Continuar Viendo',
      sessions: sessions.filter((s) => !s.isArchived),
      isComingSoon: false,
    };
  }

  if (rowId === TRAINING_ROW_SLUGS.ALL_SESSIONS) {
    const sessions = await fetchSessions({ limit: 200 });
    return {
      title: 'Todos los Videos',
      sessions,
      isComingSoon: false,
    };
  }

  const path = await fetchPathWithSessions(rowId);
  if (!path) return null;

  return {
    title: path.name,
    description: path.description,
    sessions: path.sessions,
    isComingSoon: path.isComingSoon,
  };
}

export async function fetchContinueWatchingSessions(userId, limit = 10) {
  const { data: completions, error: compError } = await supabase
    .from('gfi_training_session_completions')
    .select('session_id, percent_watched, watched')
    .eq('user_id', userId)
    .eq('watched', false)
    .gt('percent_watched', 0)
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (compError) throw compError;
  if (!completions?.length) return [];

  const sessionIds = completions.map((c) => c.session_id);
  const { data: sessions, error } = await supabase
    .from('gfi_training_sessions')
    .select(SESSION_SELECT)
    .in('id', sessionIds)
    .eq('status', TRAINING_CONTENT_STATUS.PUBLISHED)
    .is('archived_at', null);

  if (error) throw error;

  const byId = Object.fromEntries((sessions ?? []).map((s) => [s.id, mapSessionRow(s)]));
  return sessionIds.map((id) => byId[id]).filter(Boolean);
}

export async function fetchSessionComments(sessionId) {
  const { data, error } = await supabase
    .from('gfi_training_session_comments')
    .select(`
      id,
      body,
      created_at,
      user_id,
      author:gfi_profiles!gfi_training_session_comments_user_id_fkey (
        id,
        full_name,
        avatar_url
      )
    `)
    .eq('session_id', sessionId)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data ?? []).map((row) => {
    const author = mapProfile(row.author);
    const name = author?.full_name || 'Miembro';
    return {
      id: row.id,
      user: name,
      avatar: name.charAt(0).toUpperCase(),
      text: row.body,
      createdAt: row.created_at,
      userId: row.user_id,
    };
  });
}

export async function addSessionComment(sessionId, body) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');

  const { data, error } = await supabase
    .from('gfi_training_session_comments')
    .insert({ session_id: sessionId, user_id: user.id, body: body.trim() })
    .select(`
      id,
      body,
      created_at,
      user_id,
      author:gfi_profiles!gfi_training_session_comments_user_id_fkey (
        full_name
      )
    `)
    .single();

  if (error) throw error;

  const author = mapProfile(data.author);
  const name = author?.full_name || 'Tú';
  return {
    id: data.id,
    user: name,
    avatar: name.charAt(0).toUpperCase(),
    text: data.body,
    createdAt: data.created_at,
    userId: data.user_id,
  };
}

export async function deleteSessionComment(commentId) {
  const { error } = await supabase
    .from('gfi_training_session_comments')
    .delete()
    .eq('id', commentId);
  if (error) throw error;
}

export async function upsertSessionCompletion(sessionId, { watched, percentWatched }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');

  const payload = {
    user_id: user.id,
    session_id: sessionId,
    watched: Boolean(watched),
    percent_watched: Math.min(100, Math.max(0, percentWatched ?? 0)),
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from('gfi_training_session_completions')
    .upsert(payload, { onConflict: 'user_id,session_id' });

  if (error) throw error;
}

export function getVideoMetadata(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const durationSeconds = Math.round(video.duration);
      const width = video.videoWidth;
      const height = video.videoHeight;
      URL.revokeObjectURL(url);
      resolve({
        durationSeconds,
        width,
        height,
        aspectRatio: computeAspectRatio(width, height),
      });
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer la duración del video'));
    };
    video.src = url;
  });
}

export function getVideoMetadataFromUrl(videoUrl) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';
    video.onloadedmetadata = () => {
      const width = video.videoWidth;
      const height = video.videoHeight;
      resolve({
        width,
        height,
        aspectRatio: computeAspectRatio(width, height),
      });
    };
    video.onerror = () => reject(new Error('No se pudo leer las dimensiones del video'));
    video.src = videoUrl;
  });
}

export { uploadVideoToPublitioDirect as uploadVideoToPublitio } from './publitio';

export async function checkCanEditSessionChapters(originalUploaderId) {
  const { data, error } = await supabase.rpc('gfi_can_edit_session_chapters', {
    p_original_uploader: originalUploaderId,
  });
  if (error) throw error;
  return Boolean(data);
}

export async function checkCanEditReel(originalUploaderId) {
  const { data, error } = await supabase.rpc('gfi_can_edit_reel', {
    p_original_uploader: originalUploaderId,
  });
  if (error) throw error;
  return Boolean(data);
}

export async function checkCanViewTrainingArchive() {
  const { data, error } = await supabase.rpc('gfi_can_view_training_archive');
  if (error) throw error;
  return Boolean(data);
}

export async function checkIsAdminOrHigher() {
  const { data, error } = await supabase.rpc('gfi_is_admin_or_higher');
  if (error) throw error;
  return Boolean(data);
}

export async function updateSessionMetadata(sessionId, {
  title,
  summary,
  speakerName,
  tags,
  level,
  transcript,
}) {
  const payload = {
    title,
    summary,
    speaker_name: speakerName,
    tags: tags ?? [],
    level,
  };
  if (transcript !== undefined) {
    payload.transcript = transcript ?? '';
  }

  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .update(payload)
    .eq('id', sessionId)
    .select(SESSION_SELECT)
    .single();

  if (error) throw error;
  return mapSessionRow(data);
}

export async function archiveSession(sessionId) {
  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', sessionId)
    .select(SESSION_SELECT)
    .single();

  if (error) throw error;
  return mapSessionRow(data);
}

export async function restoreSession(sessionId) {
  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .update({ archived_at: null })
    .eq('id', sessionId)
    .select(SESSION_SELECT)
    .single();

  if (error) throw error;
  return mapSessionRow(data);
}

export async function deleteSessionPermanently(sessionId) {
  const { error } = await supabase
    .from('gfi_training_sessions')
    .delete()
    .eq('id', sessionId);

  if (error) throw error;
}

export async function fetchArchivedSessions({ limit = 50 } = {}) {
  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .select(SESSION_SELECT)
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data ?? []).map(mapSessionRow);
}

export async function updateReelMetadata(reelId, { caption, clipStartSeconds, clipEndSeconds }) {
  const payload = {
    caption: caption?.trim() || null,
  };
  if (clipStartSeconds !== undefined) {
    payload.clip_start_seconds = clipStartSeconds;
  }
  if (clipEndSeconds !== undefined) {
    payload.clip_end_seconds = clipEndSeconds;
  }

  const { data, error } = await supabase
    .from('gfi_training_reels')
    .update(payload)
    .eq('id', reelId)
    .select(REEL_SELECT)
    .single();

  if (error) throw error;
  return mapReelRow(data);
}

export async function fetchSessionReels(sessionId) {
  const { data, error } = await supabase
    .from('gfi_training_reels')
    .select(REEL_SELECT)
    .eq('session_id', sessionId)
    .eq('status', TRAINING_CONTENT_STATUS.PUBLISHED)
    .is('archived_at', null)
    .order('clip_start_seconds', { ascending: true });

  if (error) throw error;
  return (data ?? []).map(mapReelRow);
}

export async function archiveReel(reelId) {
  const { data, error } = await supabase
    .from('gfi_training_reels')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', reelId)
    .select(REEL_SELECT)
    .single();

  if (error) throw error;
  return mapReelRow(data);
}

export async function restoreReel(reelId) {
  const { data, error } = await supabase
    .from('gfi_training_reels')
    .update({ archived_at: null })
    .eq('id', reelId)
    .select(REEL_SELECT)
    .single();

  if (error) throw error;
  return mapReelRow(data);
}

export async function deleteReelPermanently(reelId) {
  const { error } = await supabase
    .from('gfi_training_reels')
    .delete()
    .eq('id', reelId);

  if (error) throw error;
}

export async function fetchArchivedReels({ limit = 50 } = {}) {
  const { data, error } = await supabase
    .from('gfi_training_reels')
    .select(REEL_SELECT)
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data ?? []).map(mapReelRow);
}

export async function updateSessionChapters(sessionId, chapters) {
  const normalized = normalizeChapters(chapters);
  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .update({ chapters: chaptersToDb(normalized) })
    .eq('id', sessionId)
    .select('chapters')
    .single();

  if (error) throw error;
  return normalizeChapters(data.chapters);
}

export async function publishTrainingSession({
  title,
  summary,
  publitioVideoId,
  videoUrl,
  thumbnailUrl,
  durationSeconds,
  transcript,
  actionItems,
  tags,
  level,
  chapters,
  complianceConfirmed,
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');
  if (!videoUrl?.trim()) throw new Error('URL de video requerida');

  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .insert({
      title,
      summary,
      publitio_video_id: publitioVideoId,
      video_url: videoUrl.trim(),
      thumbnail_url: thumbnailUrl,
      duration_seconds: durationSeconds ?? null,
      transcript: transcript ?? '',
      action_items: actionItems ?? [],
      tags: tags ?? [],
      level,
      chapters: chaptersToDb(chapters ?? []),
      uploaded_by: user.id,
      compliance_confirmed: complianceConfirmed,
      status: TRAINING_CONTENT_STATUS.PUBLISHED,
      published_at: new Date().toISOString(),
    })
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}

/** Create a draft session immediately after Publit.io upload (metadata empty). */
export async function createSessionDraft({
  publitioVideoId,
  videoUrl,
  thumbnailUrl,
  durationSeconds,
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');
  if (!videoUrl?.trim()) throw new Error('URL de video requerida');
  if (!publitioVideoId?.trim()) throw new Error('ID de video requerido');

  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .insert({
      title: 'Borrador sin título',
      summary: '',
      publitio_video_id: publitioVideoId.trim(),
      video_url: videoUrl.trim(),
      thumbnail_url: thumbnailUrl ?? null,
      duration_seconds: durationSeconds ?? null,
      transcript: '',
      action_items: [],
      tags: [],
      level: 'both',
      chapters: [],
      uploaded_by: user.id,
      compliance_confirmed: false,
      status: TRAINING_CONTENT_STATUS.DRAFT,
      published_at: null,
    })
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}

/** Persist draft session metadata without publish validation. */
export async function saveSessionDraft(sessionId, {
  title,
  summary,
  transcript,
  actionItems,
  tags,
  level,
  chapters,
  complianceConfirmed,
}) {
  const payload = {
    title: title?.trim() || 'Borrador sin título',
    summary: summary ?? '',
    transcript: transcript ?? '',
    action_items: actionItems ?? [],
    tags: tags ?? [],
    level: level ?? 'both',
    chapters: chaptersToDb(chapters ?? []),
    compliance_confirmed: Boolean(complianceConfirmed),
    status: TRAINING_CONTENT_STATUS.DRAFT,
    published_at: null,
  };

  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .update(payload)
    .eq('id', sessionId)
    .eq('status', TRAINING_CONTENT_STATUS.DRAFT)
    .select(SESSION_SELECT)
    .single();

  if (error) throw error;
  return mapSessionRow(data);
}

/** Publish an existing draft session after completeness validation (client + DB). */
export async function publishSessionDraft(sessionId, {
  title,
  summary,
  transcript,
  actionItems,
  tags,
  level,
  chapters,
  complianceConfirmed,
}) {
  if (!title?.trim()) throw new Error('El título es obligatorio');
  if (!summary?.trim()) throw new Error('El resumen es obligatorio');
  if (!complianceConfirmed) throw new Error('Debes confirmar el cumplimiento de políticas');

  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .update({
      title: title.trim(),
      summary,
      transcript: transcript ?? '',
      action_items: actionItems ?? [],
      tags: tags ?? [],
      level: level ?? 'both',
      chapters: chaptersToDb(chapters ?? []),
      compliance_confirmed: true,
      status: TRAINING_CONTENT_STATUS.PUBLISHED,
      published_at: new Date().toISOString(),
    })
    .eq('id', sessionId)
    .eq('status', TRAINING_CONTENT_STATUS.DRAFT)
    .select(SESSION_SELECT)
    .single();

  if (error) throw error;
  return mapSessionRow(data);
}

export async function createSessionReels({
  sessionId,
  publitioVideoId,
  videoUrl,
  thumbnailUrl,
  clips,
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');
  if (!videoUrl?.trim()) throw new Error('URL de video requerida');

  const rows = clips.map((clip) => ({
    session_id: sessionId,
    publitio_video_id: publitioVideoId,
    video_url: videoUrl.trim(),
    thumbnail_url: thumbnailUrl,
    clip_start_seconds: clip.clipStartSeconds,
    clip_end_seconds: clip.clipEndSeconds,
    caption: clip.caption,
    uploaded_by: user.id,
    status: TRAINING_CONTENT_STATUS.PUBLISHED,
    published_at: new Date().toISOString(),
    ...cropSettingsToDb({
      sourceAspectRatio: clip.sourceAspectRatio,
      cropMode: clip.cropMode,
      focalX: clip.focalX,
      focalY: clip.focalY,
    }),
  }));

  const { data, error } = await supabase
    .from('gfi_training_reels')
    .insert(rows)
    .select(REEL_SELECT);

  if (error) throw error;
  return (data ?? []).map(mapReelRow);
}

export async function createStandaloneReel({
  publitioVideoId,
  videoUrl,
  thumbnailUrl,
  caption,
  sourceAspectRatio,
  cropMode,
  focalX,
  focalY,
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');
  if (!videoUrl?.trim()) throw new Error('URL de video requerida');

  const { data, error } = await supabase
    .from('gfi_training_reels')
    .insert({
      publitio_video_id: publitioVideoId,
      video_url: videoUrl.trim(),
      thumbnail_url: thumbnailUrl,
      caption,
      uploaded_by: user.id,
      session_id: null,
      clip_start_seconds: null,
      clip_end_seconds: null,
      status: TRAINING_CONTENT_STATUS.PUBLISHED,
      published_at: new Date().toISOString(),
      ...cropSettingsToDb({ sourceAspectRatio, cropMode, focalX, focalY }),
    })
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}

/** Create a draft standalone reel immediately after Publit.io upload. */
export async function createReelDraft({
  publitioVideoId,
  videoUrl,
  thumbnailUrl,
  sourceAspectRatio,
  cropMode,
  focalX,
  focalY,
}) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');
  if (!videoUrl?.trim()) throw new Error('URL de video requerida');
  if (!publitioVideoId?.trim()) throw new Error('ID de video requerido');

  const { data, error } = await supabase
    .from('gfi_training_reels')
    .insert({
      publitio_video_id: publitioVideoId.trim(),
      video_url: videoUrl.trim(),
      thumbnail_url: thumbnailUrl ?? null,
      caption: null,
      uploaded_by: user.id,
      session_id: null,
      clip_start_seconds: null,
      clip_end_seconds: null,
      status: TRAINING_CONTENT_STATUS.DRAFT,
      published_at: null,
      ...cropSettingsToDb({ sourceAspectRatio, cropMode, focalX, focalY }),
    })
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}

/** Persist draft reel metadata without publish validation. */
export async function saveReelDraft(reelId, {
  caption,
  clipStartSeconds,
  clipEndSeconds,
  sourceAspectRatio,
  cropMode,
  focalX,
  focalY,
}) {
  const payload = {
    caption: caption?.trim() || null,
    clip_start_seconds: clipStartSeconds ?? null,
    clip_end_seconds: clipEndSeconds ?? null,
    status: TRAINING_CONTENT_STATUS.DRAFT,
    published_at: null,
    ...cropSettingsToDb({ sourceAspectRatio, cropMode, focalX, focalY }),
  };

  const { data, error } = await supabase
    .from('gfi_training_reels')
    .update(payload)
    .eq('id', reelId)
    .eq('status', TRAINING_CONTENT_STATUS.DRAFT)
    .select(REEL_SELECT)
    .single();

  if (error) throw error;
  return mapReelRow(data);
}

/** Publish an existing draft reel after completeness validation. */
export async function publishReelDraft(reelId, {
  caption,
  clipStartSeconds,
  clipEndSeconds,
  sourceAspectRatio,
  cropMode,
  focalX,
  focalY,
}) {
  if (!caption?.trim()) throw new Error('Añade un texto o caption para el reel');

  const { data, error } = await supabase
    .from('gfi_training_reels')
    .update({
      caption: caption.trim(),
      clip_start_seconds: clipStartSeconds ?? null,
      clip_end_seconds: clipEndSeconds ?? null,
      status: TRAINING_CONTENT_STATUS.PUBLISHED,
      published_at: new Date().toISOString(),
      ...cropSettingsToDb({ sourceAspectRatio, cropMode, focalX, focalY }),
    })
    .eq('id', reelId)
    .eq('status', TRAINING_CONTENT_STATUS.DRAFT)
    .select(REEL_SELECT)
    .single();

  if (error) throw error;
  return mapReelRow(data);
}

export async function fetchMyDraftSessions() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');

  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .select(SESSION_SELECT)
    .eq('status', TRAINING_CONTENT_STATUS.DRAFT)
    .eq('uploaded_by', user.id)
    .is('archived_at', null)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return (data ?? []).map(mapSessionRow);
}

export async function fetchMyDraftReels() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('No autenticado');

  const { data, error } = await supabase
    .from('gfi_training_reels')
    .select(REEL_SELECT)
    .eq('status', TRAINING_CONTENT_STATUS.DRAFT)
    .eq('uploaded_by', user.id)
    .is('archived_at', null)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return (data ?? []).map(mapReelRow);
}

export async function fetchStaleDraftDays() {
  const { data, error } = await supabase.rpc('gfi_training_stale_draft_days');
  if (error) throw error;
  return Number(data) || 14;
}

export async function fetchStaleTrainingDrafts(staleDays = null) {
  const { data, error } = await supabase.rpc('gfi_list_stale_training_drafts', {
    stale_days: staleDays,
  });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    contentType: row.content_type,
    id: row.id,
    title: row.title || (row.content_type === 'session' ? 'Sesión sin título' : 'Reel sin caption'),
    uploadedBy: row.uploaded_by,
    uploaderName: row.uploader_name || '—',
    updatedAt: row.updated_at,
    createdAt: row.created_at,
    daysStale: row.days_stale,
    thumbnailUrl: row.thumbnail_url,
  }));
}

export async function fetchDistinctTags() {
  const { data, error } = await supabase
    .from('gfi_training_sessions')
    .select('tags')
    .eq('status', TRAINING_CONTENT_STATUS.PUBLISHED)
    .is('archived_at', null);
  if (error) throw error;

  const set = new Set();
  (data ?? []).forEach((row) => {
    (row.tags ?? []).forEach((tag) => {
      if (tag?.trim()) set.add(tag.trim());
    });
  });
  return [...set].sort((a, b) => a.localeCompare(b, 'es'));
}
